import { difference, groupBy, intersection, uniq } from 'lodash';
import flatten from 'lodash/flatten';
import snakeCase from 'lodash/snakeCase';
import { computed } from 'vue';

import { RoleApi } from 'SharedDir/backend/api';
import useCustomLoading from 'SharedDir/common/composables/useCustomLoading';
import { useNotifications } from 'SharedDir/common/composables/useNotifications';
import { makeRequest } from 'SharedDir/common/composables/useRequests';
import { ROLE_SUPERADMINISTRATOR } from 'SharedDir/constants/role';
import { UserInfo } from 'SharedDir/declarations/access';
import { PermissionTranslated } from 'SharedDir/declarations/role';
import PermissionSet from 'SharedDir/service/permissions/PermissionSet';
import { useUserStore } from 'SharedDir/stores/user.store';

const quotifiedList = (list: Array<string>): string => list.map((l) => `"${l}"`).join(', ');

class ErrorBag extends Error {
  errors: Array<string> = [];

  name: string = 'Набор разрешений не совпадает!!!';

  message: string = 'Набор разрешений не совпадает!!!';

  toString(): string {
    const details = this.errors.join('\n');
    return `${this.name}\n${details}`;
  }
}

export function isSuperuser(user: UserInfo): boolean {
  return user.roleId === ROLE_SUPERADMINISTRATOR.id;
}

export const hasPermission = (
  permission: string | string[],
  strict = false,
  user: UserInfo
): boolean => {
  if (!user) {
    return false;
  }
  if (isSuperuser(user)) {
    return true;
  }

  const permissionList = typeof permission === 'string' ? [permission] : permission;
  const userPermissionsList = user.role?.permissions ?? [];
  let isGranted = false;

  if (strict) {
    isGranted = permissionList.every((p) => {
      const upm = userPermissionsList.find((up) => up === p);
      return !!upm;
    });
  } else {
    isGranted = permissionList.some((p) => {
      const upm = userPermissionsList.find((up) => up === p);
      return !!upm;
    });
  }

  return isGranted;
};

const canPermission = (permissions: Array<string>, strict = false, user: UserInfo): boolean => {
  return hasPermission(permissions, strict, user) || user?.roleId === ROLE_SUPERADMINISTRATOR.id;
};

export function usePermissions() {
  const userStore = useUserStore();
  const user = computed(() => userStore.getUser as UserInfo);
  const permissionSet = PermissionSet;
  /**
   * Является ли текущий пользователь - суперпольхователем.
   * @type {ComputedRef<boolean>}
   */
  const isRoot = computed(() => user.value.roleId === ROLE_SUPERADMINISTRATOR.id);

  const can = (...args: Array<string | Array<string>>): boolean => {
    const flatPermissions = flatten(args);
    const permitted = canPermission(flatPermissions, true, user.value);
    if (!permitted && user.value) {
      const list = quotifiedList(flatPermissions);
      console.warn(`${user.value?.login} failed permission check(strict) for [${list}]`);
    }
    return permitted;
  };

  const canAny = (...args: Array<string | Array<string>>): boolean => {
    const flatPermissions = flatten(args);
    const permitted = canPermission(flatPermissions, false, user.value);
    if (!permitted && user.value) {
      const list = quotifiedList(flatPermissions);
      console.warn(`${user.value?.login} failed permission check(any) for [${list}]`);
    }
    return permitted;
  };

  const { loading: checking, requestOptions } = useCustomLoading();

  const getOpenActionTitle = (permission: string) =>
    can(permission) ? 'Редактировать' : 'Открыть';

  function checkPermissions(): Promise<void> {
    const { successNotification } = useNotifications();
    return makeRequest(async () => {
      const allAvailablePermissions = await RoleApi.getAllPermissions();
      const groupedPermissions: { [key: string]: Array<PermissionTranslated['permission']> } =
        groupBy(
          allAvailablePermissions.map((p) => p.permission),
          (p) => p.split('.')[0]
        );
      const modules = Object.keys(permissionSet);
      const bag = new ErrorBag();
      for (let i = 0; i < modules.length; i++) {
        const moduleName = modules[i];
        const snakedModuleName = snakeCase(moduleName);
        if (!groupedPermissions[snakedModuleName]) {
          bag.errors.push(`На фронте неизвестная группа разрешений ${moduleName}`);
          // eslint-disable-next-line no-continue
          continue;
        }
        const frontPermissions = permissionSet[moduleName].all;
        const backPermissions = uniq(groupedPermissions[snakedModuleName]);
        const common = intersection(frontPermissions, backPermissions);
        if (
          frontPermissions.length !== backPermissions.length ||
          common.length !== frontPermissions.length
        ) {
          bag.errors.push(
            `Для модуля ${moduleName} разрешения на фронте [${quotifiedList(
              difference(frontPermissions, backPermissions)
            )}] лишние, и не хватает [${quotifiedList(
              difference(backPermissions, frontPermissions)
            )}]`
          );
        }
        delete groupedPermissions[snakedModuleName];
      }
      const unknownModules = Object.keys(groupedPermissions);
      if (unknownModules.length > 0) {
        bag.errors.push(
          `На фронте не хватает модулей которые есть на бекенде [${quotifiedList(unknownModules)}]`
        );
      }
      if (bag.errors.length > 0) {
        console.error(String(bag));
        throw bag;
      }
      successNotification('Набор разрешений совпадает!');
    }, requestOptions);
  }

  return {
    can,
    canAny,
    isRoot,
    checking,
    checkPermissions,
    permissionSet,
    getOpenActionTitle,
  };
}
